import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash';
import {EMPTY, Observable, of, throwError, timer} from 'rxjs';
import {AppRedirectService} from '../app-redirect.service';
import {getLoginUrl, getLogoutUrl} from '../auth.urls';
import {AuthenticationService} from '../authentication.service';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment.dev';

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor {
    private calledLogout = false;
    private interruptLogout = false;

    constructor(private injector: Injector) {
        // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        // Ak bude potrebne pouzit v interceptore nejaku service, tak referenciu tahat cez Injector, nie cez DI konstruktora!
        // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    }

    intercept(req: HttpRequest<any>, next: HttpHandler) {

        const authenticationService: AuthenticationService = this.injector.get(AuthenticationService);

        const appRedirectService: AppRedirectService = this.injector.get(AppRedirectService);

        const headersToSet = {};

        const access_token = AuthenticationService.getStoredAuthData()?.access_token || null;

        if (req.headers) {
            if (!req.headers.has('Content-Type')) {
                headersToSet['Content-Type'] = 'application/json';
            }

            if (access_token && !req.headers.has('Authorization')) {

                headersToSet['Authorization'] = 'Bearer ' + access_token;
            }
        }

        req = req.clone({
            setHeaders: headersToSet,
        });

        return next.handle(req)
            .pipe(tap({
                next: (response) => {
                    if (response instanceof HttpResponse) {
                        if (response.status === 200) {
                            if (_.startsWith(response.url, getLogoutUrl())) {
                                this.logout(response, authenticationService, appRedirectService);
                            }
                        }
                    }
                },
            }))
            .pipe(
                catchError((error) => {

                    const errorUrl = (error instanceof HttpErrorResponse ? error.url : null) || req.url;
                    const isCurrentApiRequest = _.startsWith(errorUrl, environment.API_URL);

                    if (!(error instanceof HttpErrorResponse) || !isCurrentApiRequest) {
                        return throwError(error.error);
                    }

                    if (_.startsWith(errorUrl, getLoginUrl())) {
                        if (error.status === 400 || ([401, 404].includes(error.status) && this.isRefreshRequest(req))) {
                            this.logout(error, authenticationService, appRedirectService);
                            return EMPTY;
                        }
                        return throwError(() => error);
                    } else if (_.startsWith(errorUrl, getLogoutUrl())) {
                        return throwError(error.error);
                    } else if (error.status === 401) {
                        if (AuthenticationService.isSessionExpired()) {
                            this.logout(error, authenticationService, appRedirectService);
                            return EMPTY;
                        } else if (!AuthenticationService.checkExpirationTime() && AuthenticationService.checkRefreshExpirationTime()) {
                            return this.refreshToken(authenticationService).pipe(
                                catchError(refreshError => {
                                    if (this.shouldLogoutAfterRefreshError(refreshError)) {
                                        this.logout(refreshError, authenticationService, appRedirectService);
                                        return EMPTY;
                                    }

                                    return throwError(() => refreshError);
                                }),
                                switchMap(() => {
                                    const newToken = AuthenticationService.getStoredAuthData()?.access_token || null;
                                    const retryReq = req.clone({
                                        setHeaders: {
                                            Authorization: newToken ? 'Bearer ' + newToken : '',
                                        },
                                    });
                                    return next.handle(retryReq).pipe(
                                        catchError(retryErr => {
                                            if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
                                                this.logout(retryErr, authenticationService, appRedirectService);
                                                return EMPTY;
                                            }
                                            return throwError(() => retryErr);
                                        }),
                                    );
                                }),
                            );
                        } else {
                            this.logout(error, authenticationService, appRedirectService);
                            return EMPTY;
                        }
                    } else if (error.status === 503 && error.error?.error === 'AUTH_SERVICE_UNAVAILABLE') {
                        const retryCount = +(req.headers.get('X-Auth-Retry') ?? '0');
                        if (retryCount < 5) {
                            const retryAfterSec = +(error.headers?.get('Retry-After') ?? '3');
                            const retryReq = req.clone({ setHeaders: { 'X-Auth-Retry': String(retryCount + 1) } });
                            return timer(retryAfterSec * 1000).pipe(
                                switchMap(() => next.handle(retryReq))
                            );
                        }
                        return throwError((error as HttpErrorResponse).error || error);
                    } else if (error.status < 200 || error.status > 299) {
                        // TODO: Zobrazit error toast ak je potrebne ked sa vyskytne neocakavany stav alebo chyba protokolu
                        return throwError((error as HttpErrorResponse).error || error);
                    }
                }),
            );
    }

    setInterruptLogout(interruptLogout: boolean) {
        this.interruptLogout = interruptLogout;
    }

    private isRefreshRequest(req: HttpRequest<any>): boolean {
        const body = req.body as any;

        return _.startsWith(req.url, getLoginUrl())
            && !!body
            && !_.isNil(body.refreshToken);
    }

    private shouldLogoutAfterRefreshError(error: unknown): boolean {
        if (!(error instanceof HttpErrorResponse)) {
            return true;
        }

        return [400, 401, 403, 404].includes(error.status);
    }

    private logout(
        response: HttpResponse<any> | HttpErrorResponse,
        authenticationService: AuthenticationService,
        appRedirectService: AppRedirectService,
    ): void {

        if (this.interruptLogout) {
            return;
        }

        if (this.calledLogout) {
            return;
        }

        this.calledLogout = true;
        authenticationService.logout().subscribe({
            next: () => {
                this.calledLogout = false;
                appRedirectService.redirectLogin(false);
            },
            error: error => {
                this.calledLogout = false;
                appRedirectService.redirectLogin(false);
            },
        });
    }

    private refreshToken(authenticationService: AuthenticationService): Observable<any> {
        if (authenticationService.tokenRefreshInProgress) {
            return authenticationService.tokenRefreshed$.pipe(
                take(1),
                switchMap(result => result ? of(result) : throwError(() => 'Token refresh failed')),
            );
        } else {
            const currentSession = AuthenticationService.getStoredAuthData();

            if (_.isNil(currentSession)) {
                return throwError('No session present!');
            }

            return authenticationService
                .getNewToken(currentSession.user.login, currentSession.refresh_token, currentSession.session_guid)
                .pipe(
                    tap({
                        next: (d) => {
                            authenticationService.emitTokenRefreshed(d);
                        },
                        error: error => {
                            authenticationService.emitTokenRefreshFailed(error);
                        },
                    }),
                );
        }
    }
}

